import React, {useState} from "react";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import SvgIcon from "connectui/component/display/SvgIcon";
import ChipText from "connectui/component/display/chip/ChipText";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import Text from "connectui/component/display/Text";
import Container from "connectui/component/layout/Container";
import FlexContainer from "connectui/component/layout/FlexContainer";
import {getBookingMergeConflicts} from "redux/actions/bookingsActions";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";
import ICONS from "connectui/icons/iconMap";
import {useTheme} from "connectui/theme/ThemeProvider";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import ErrorCardBase from "connectui/component/display/card/base/ErrorCardBase";

const MergeCandidatesCard = ({
  guest,
  bookings,
  onMergeConflictsFound,
  onError,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {theme} = useTheme();
  const [hiddenCards, setHiddenCards] = useState(new Set());
  const [isFetchingConflicts, setIsFetchingConflicts] = useState(false);

  const handleErrorAlertClose = () => dispatch(closeGeneralError());

  // Combine all merge candidates from guest and bookings
  const allMergeCandidates = [
    ...(guest?.metadata?.merge_candidates || []).map((candidate) => ({
      ...candidate,
      source: "guest",
    })),
    ...(bookings || [])
      .filter(
        (booking) =>
          booking.merged_from === null && booking.merged_into === null,
      )
      .flatMap((booking) =>
        (booking.merge_candidates || []).map((candidate) => ({
          ...candidate,
          source: "booking",
          from_booking_id: booking.booking_id,
          from_listing_id: booking.listing_id,
        })),
      ),
  ];

  if (!guest?.merged_into === null || !guest?.merged_from === null) {
    return null;
  }

  // Find the candidate with highest similarity score
  const highestScoringCandidate = allMergeCandidates.reduce(
    (highest, current) => {
      if (!highest || current.similarity_score > highest.similarity_score) {
        return current;
      }
      return highest;
    },
    null,
  );

  const candidatesToShow = highestScoringCandidate
    ? [highestScoringCandidate]
    : [];

  return (
    <>
      {candidatesToShow.map((mergeCandidate, index) => {
        if (hiddenCards.has(mergeCandidate.booking_id)) {
          return null;
        }

        return (
          <SimpleCard
            key={`merge-card-${mergeCandidate.source}-${mergeCandidate.booking_id}-${index}`}
            cardBase={ErrorCardBase}
            sx={{
              padding: theme.spacing(3),
              marginBottom: theme.spacing(2),
              position: "relative",
              overflow: "visible",
            }}
            description={
              <>
                <Container sx={{position: "relative", width: "100%"}}>
                  <Container
                    sx={{
                      position: "sticky",
                      top: 0,
                      float: "right",
                      marginTop: theme.spacing(-2),
                      marginRight: theme.spacing(-2),
                      zIndex: 1,
                    }}
                  >
                    <ButtonIcon
                      size="small"
                      onClick={() => {
                        setHiddenCards(
                          (prev) =>
                            new Set([...prev, mergeCandidate.booking_id]),
                        );
                      }}
                      sx={{
                        padding: theme.spacing(0.5),
                        color: theme.colors.error600,
                        backgroundColor: theme.colors.error100,
                        "&:hover": {
                          backgroundColor: theme.colors.error200,
                        },
                      }}
                      icon={
                        <SvgIcon
                          component={ICONS.x}
                          sx={{height: "10px", width: "10px"}}
                        />
                      }
                    />
                  </Container>
                  <Text as="span" color="theme.colors.error600" weight="bold">
                    {guest?.name}
                  </Text>{" "}
                  and{" "}
                  <Text as="span" color="theme.colors.error600" weight="bold">
                    {mergeCandidate?.guest_name}{" "}
                  </Text>
                  {t("merge-guest.chip")}
                </Container>
              </>
            }
            actionRow={
              <FlexContainer alignItems="center" gap={theme.spacing(2)}>
                <ChipText
                  text={t("merge-guest.chip-button")}
                  color="textPrimary"
                  sx={{
                    backgroundColor: theme.colors.error200,
                    color: theme.colors.error600,
                    cursor: isFetchingConflicts ? "wait" : "pointer",
                    opacity: isFetchingConflicts ? 0.7 : 1,
                  }}
                  onClick={() => {
                    setIsFetchingConflicts(true);

                    const fromCandidate = {
                      guest_id: guest.guest_id,
                      booking_id: mergeCandidate.from_booking_id ?? null,
                      listing_id: mergeCandidate.from_listing_id ?? null,
                      guest_name: guest.name,
                    };

                    const toCandidate = {
                      guest_id: mergeCandidate.guest_id,
                      booking_id: mergeCandidate.booking_id ?? null,
                      listing_id: mergeCandidate.listing_id ?? null,
                      guest_name: mergeCandidate.guest_name,
                    };

                    dispatch(
                      getBookingMergeConflicts(
                        {
                          from_merge_candidate: fromCandidate,
                          to_merge_candidate: toCandidate,
                        },
                        (response) => {
                          setIsFetchingConflicts(false);
                          const hasNoConflicts =
                            !response.conflicts.guest?.conflicts?.length &&
                            !response.conflicts.guest?.auto_resolved_fields
                              ?.length &&
                            !response.conflicts.booking?.conflicts?.length &&
                            !response.conflicts.booking?.auto_resolved_fields
                              ?.length &&
                            !response.conflicts.listing?.conflicts?.length &&
                            !response.conflicts.listing?.auto_resolved_fields
                              ?.length;

                          if (hasNoConflicts) {
                            dispatch(
                              openGeneralError({
                                message: "Cannot merge these guests",
                                subtitle:
                                  "Please contact support if you want to merge these guests.",
                                open: true,
                                disableSeverity: true,
                                onClose: handleErrorAlertClose,
                              }),
                            );
                            return;
                          }

                          onMergeConflictsFound(response);
                        },
                        (error) => {
                          dispatch(
                            openGeneralError({
                              message: "Failed to fetch merge conflicts",
                              subtitle: error.message,
                              open: true,
                              onClose: handleErrorAlertClose,
                            }),
                          );
                          setIsFetchingConflicts(false);
                          if (onError) {
                            onError(error);
                          }
                        },
                      ),
                    );
                  }}
                  disabled={isFetchingConflicts}
                />
              </FlexContainer>
            }
          />
        );
      })}
    </>
  );
};

export default MergeCandidatesCard;
